import React, { SyntheticEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { Module } from '../models/module';
import { clearAuthToken, setAuthToken } from '../utils/auth-storage';
import { COMPANY_CODE, clearCompanyCode, companyFromHost, getCompanyCode, setCompanyCode, signupUrl } from '../utils/company';
import { useT } from '../i18n';
import { useBrandName } from '../components/BrandMark';
import { AuthLayout } from '@routes-plan/ui';
import { clearCompanyBrand, loadCompanyBrand } from '../utils/company-brand';
import { RouteScene } from '../components/RouteScene';

interface Props {
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  setModules: React.Dispatch<React.SetStateAction<Module[]>>;
}

/**
 * Ingreso a la web de una empresa. Si la dirección no trae la empresa (subdominio), se pide su código
 * y se recuerda en este navegador.
 */
const SignInSide: React.FC<Props> = ({ setIsAuthenticated, setModules }) => {
  const t = useT();
  const brandName = useBrandName();
  const navigate = useNavigate();
  const fromHost = companyFromHost();
  const [company, setCompany] = useState(getCompanyCode() ?? '');
  const [editingCompany, setEditingCompany] = useState(!fromHost && !getCompanyCode());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setError('');
    setCompanyError('');
    const code = (fromHost ?? company).trim().toLowerCase();
    if (!fromHost && !COMPANY_CODE.test(code)) {
      setCompanyError(t('login.companyFormat'));
      setEditingCompany(true);
      return;
    }
    if (!username.trim() || !password) {
      setError(t('login.missing'));
      return;
    }
    if (!fromHost) setCompanyCode(code);
    // La marca de la empresa (colores y logo) se pinta en cuanto el API la devuelve.
    loadCompanyBrand(code);
    setSending(true);
    try {
      const response = await axios.post('login', {
        username: username.trim(),
        password,
        companyCode: code,
      });
      if (response.data?.token) setAuthToken(response.data.token);
      else clearAuthToken();
      sessionStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
      setModules(response.data.data);
      sessionStorage.setItem('modules', JSON.stringify(response.data.data));
      localStorage.setItem('modules', JSON.stringify(response.data.data));
      navigate('/');
    } catch (e: any) {
      const data = e?.response?.data;
      const status = e?.response?.status;
      setPassword('');
      if (data?.code === 'COMPANY_REQUIRED') {
        // La empresa no existe o aún se está preparando: se vuelve a pedir el código.
        if (!fromHost) clearCompanyCode();
        setEditingCompany(!fromHost);
        setCompanyError(t('login.companyNotFound'));
      } else if (data?.code === 'COMPANY_CANCELLED') {
        setError(t('login.companyCancelled'));
      } else if (status === 400) {
        setError(t('login.badCredentials'));
      } else if (status === 403 && data?.message) {
        setError(data.message);
      } else if (!e?.response) {
        setError(t('login.network'));
      } else {
        setError(t('login.unexpected'));
      }
    } finally {
      setSending(false);
    }
  };

  document.title = `${t('login.title')} - ${brandName}`;
  const shownCompany = fromHost ?? company;

  return (
    <>
      <CssBaseline />
      {/* Misma pantalla de ingreso que el panel y el portal; al lado, la ruta del día animada. */}
      <AuthLayout variant="split" title={t('login.title')} subtitle={t('login.subtitle')} asideBleed aside={<RouteScene />}>
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={submit} sx={{ display: 'grid', gap: 2 }}>
            {editingCompany && !fromHost ? (
              <TextField
                id="company"
                label={t('login.company')}
                value={company}
                onChange={(e) => {
                  setCompany(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]/g, '')
                      .slice(0, 20),
                  );
                  setCompanyError('');
                }}
                error={!!companyError}
                helperText={companyError || t('login.companyHint')}
                autoComplete="organization"
                inputProps={{ autoCapitalize: 'none', spellCheck: false }}
                autoFocus
                fullWidth
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('login.companyLabel')}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{shownCompany}</Typography>
                </Box>
                {!fromHost && (
                  <Button
                    size="small"
                    onClick={() => {
                      clearCompanyBrand();
                      setEditingCompany(true);
                    }}
                  >
                    {t('login.changeCompany')}
                  </Button>
                )}
                {companyError && (
                  <Typography variant="body2" color="error" sx={{ width: '100%' }}>
                    {companyError}
                  </Typography>
                )}
              </Box>
            )}
            <TextField
              id="username"
              name="username"
              label={t('login.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              inputProps={{ autoCapitalize: 'none', spellCheck: false }}
              autoFocus={!editingCompany || !!fromHost}
              fullWidth
            />
            <TextField
              id="password"
              name="password"
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="large" disabled={sending} fullWidth sx={{ mt: 1, minHeight: 48 }}>
              {sending ? <CircularProgress size={22} color="inherit" /> : t('login.submit')}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
            {t('login.noCompany')}{' '}
            <Link href={signupUrl()} underline="hover">
              {t('login.createCompany')}
            </Link>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 2 }}>
            © {new Date().getFullYear()} {brandName}
          </Typography>
        </Box>
      </AuthLayout>
    </>
  );
};

export default SignInSide;
